import classNames from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { DeleteApprover } from 'legacy/lut/components/widgets/public/partner360/commitment/actions/DeleteApprover'
import { CommitmentItemStatus } from 'legacy/lut/components/widgets/public/partner360/commitment/CommitmentItemStatus'
import { PlanChangesLogDialog } from 'legacy/lut/components/widgets/public/partner360/commitment/planChangesLogDialog/PlanChangesLogDialog'
import { selectProgramType } from 'legacy/lut/features/partner360/configuration'
import { selectSectionSettings } from 'legacy/lut/features/partner360/sectionSettings'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import { appGetState } from 'legacy/lut/store'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Translation } from 'cng-common/features/translations/components/translation'
import { getUrlWithParams } from 'core-components/helpers/url'
import { Button } from 'core-components/library/button'
import { Tooltip } from 'core-components/library/tooltip'
import { getFormatDate } from 'core/classes/helpers/DateHelper'
import { isSimulation } from 'core/classes/helpers/StorageHelper'
import { useStartSimulationMutation } from 'core/store/service/simulation'
import { useActualsTypes } from 'partner360/components/actualsTypeButtons'
import { ValueTypeConfirmDialog } from 'partner360/features/partnerPlanExport'
import { getActualsValueTypeDefault } from 'partner360/helpers/sectionSettings/getActualsValueTypeDefault'
import { getExportPlanActualsValueType } from 'partner360/helpers/sectionSettings/getExportPlanActualsValueType'
import {
  ActualsValueTypes,
  DEFAULT_NETNDP_DISTRIBUTOR_TYPES,
  DEFAULT_NETNDP_TYPES,
} from 'partner360/models/actualValueTypes'

import { DateField } from '../fields/DateField'
import { SimpleField } from '../fields/SimpleField'

import { getSignOffButtonCaption } from './helpers/getSignOffButtonCaption'
import { CommitmentTypes, ICommitmentEditorItem, ICommitmentEditorProps } from './models'

import styles from './CommitmentItemEditor.module.scss'

export const CommitmentItemEditor = ({
  commitmentEditorItem: ackItem,
  ...props
}: ICommitmentEditorProps) => {
  /** -------------------- State ----------------------- */

  const [isShowChangesDialog, setIsShowChangesDialog] = useState(false)
  const [actualsValueTypeForConfirm, setActualsValueTypeForConfirm] = useState<ActualsValueTypes>(
    ActualsValueTypes.None
  )
  const [actualsValueType, setActualsValueType] = useState(ActualsValueTypes.Ndp)

  /** -------------------- Selectors ----------------------- */

  const programType = useSelector(selectProgramType)
  const sectionSettings = useSelector(selectSectionSettings)
  const [startSimulation] = useStartSimulationMutation()
  const { isP360Tester, isSuperAdmin } = useUserRoles()

  /** -------------------- Hooks ----------------------- */
  const navigate = useNavigate()
  const { pdfActualsTypes, pdfActualsTypeDefault } = useActualsTypes()

  /** -------------------- Variables ----------------------- */

  const isSimulationMode = isSimulation()

  const isRetailer = useMemo(() => programType === ProgramTypes.Retail, [programType])

  const isPartnerUser = useMemo(
    () => !!appGetState().user.roleIds.find((role: any) => role === 'Partner User'),
    []
  )

  const isPbmAck = useMemo(
    () => ackItem.CommitmentType == CommitmentTypes.PBM,
    [ackItem.CommitmentType]
  )

  const isManagerAck = useMemo(
    () => ackItem.CommitmentType === CommitmentTypes.Manager,
    [ackItem.CommitmentType]
  )

  const isPartnerAck = useMemo(
    () => ackItem.CommitmentType === CommitmentTypes.Partner,
    [ackItem.CommitmentType]
  )

  /** -------------------- User actions handlers ----------------------- */

  const openChangesLogDialog = useCallback(async () => {
    setIsShowChangesDialog(true)
  }, [])

  const closeConfirmActualsValueTypeDialogHandler = useCallback(() => {
    setActualsValueTypeForConfirm(ActualsValueTypes.None)
  }, [])

  const confirmActualsValueTypeHandler = useCallback(async () => {
    props.showSignOffDialog(true, ackItem)
  }, [ackItem, props])

  const onSimulate = useCallback(
    (ackItem: ICommitmentEditorItem) => () => {
      if (!ackItem.SignerEmail) {
        return
      }
      if (ackItem.CommitmentType === CommitmentTypes.Partner) {
        navigate(getUrlWithParams({ page: 'p360' }))
      }
      startSimulation(ackItem.SignerEmail)
    },
    [navigate, startSimulation]
  )
  /** -------------------- Effects ----------------------- */

  useEffect(() => {
    let availableActualsTypes: ActualsValueTypes[] = props.isDistributor
      ? DEFAULT_NETNDP_DISTRIBUTOR_TYPES
      : DEFAULT_NETNDP_TYPES

    if (pdfActualsTypes?.length) {
      availableActualsTypes = pdfActualsTypes
    }

    const exportPlanActualsValueType = getExportPlanActualsValueType(
      sectionSettings,
      programType,
      isRetailer,
      props.isDistributor,
      availableActualsTypes,
      pdfActualsTypeDefault
    )
    setActualsValueType(exportPlanActualsValueType)
  }, [
    isRetailer,
    pdfActualsTypeDefault,
    pdfActualsTypes,
    programType,
    props.isDistributor,
    sectionSettings,
  ])

  /** -------------------- Render ----------------------- */

  return (
    <div className={classNames(styles.col, styles.root)}>
      <RenderWhen condition={!isSimulationMode && (isP360Tester || isSuperAdmin)}>
        <Button onClick={onSimulate(ackItem)} variant={'outlined'} className={styles.simulationBtn}>
          Simulate
        </Button>
      </RenderWhen>

      <SimpleField
        className={styles.field}
        size="max"
        item={ackItem}
        field="SignerRole"
        label="partnercommitment-role"
        translate
      />
      <SimpleField
        className={styles.field}
        size="max"
        item={ackItem}
        field="SignerName"
        label="partnercommitment-name"
        translate
      />
      <SimpleField
        className={styles.field}
        size="max"
        item={ackItem}
        field="SignerEmail"
        label="partnercommitment-email"
        translate
        copy
      />
      <DateField
        size="sm"
        item={ackItem}
        field="ActionDate"
        label="partnercommitment-date"
        format={'DD MMM YY HH:mm'}
        value={
          ackItem.ActionDate
            ? `${getFormatDate(ackItem.ActionDate, 'date3')} ${getFormatDate(
                ackItem.ActionDate,
                'time'
              )}`
            : '-'
        }
        localFormat
        translate
      />

      <div className={styles.footerInfo}>
        <CommitmentItemStatus commitmentItem={ackItem} openChangesLog={openChangesLogDialog} />
        {ackItem.Comment && (
          <Tooltip
            disabled={ackItem.Comment.length < 60}
            tipContent={() => <span>{ackItem.Comment}</span>}
          >
            <SimpleField
              className={styles.field}
              size="max"
              item={ackItem}
              field="Comment"
              label="partnercommitment-comment"
              translate
              multiline
            />
          </Tooltip>
        )}
      </div>

      {/* ------------------------------ All commitment buttons ------------------------------
       PBM     buttons: Signoff, drop-sign
       Manager buttons: Signoff, drop-sign, refuse
          Manager Only: alert('no-manager'), manager-select, manager-change,
                        alert(manager-not-confirmed), confirm-manager, manager-edit-and-confirm
       Partner buttons: Signoff, drop-sign,
          Partner only: refuse, resend, export
       */}

      <div className={styles.footer}>
        <div className={styles.controls}>
          {/* ------------------------------ Sign-Off (PBM, Manager, Partner) ------------------------------ */}
          {!!ackItem.CanSignOff && (
            <Button
              className={styles.dropButton}
              disabled={ackItem.CanSignOff === -1}
              onClick={() => {
                const actualsValueTypeDefault =
                  pdfActualsTypeDefault ||
                  getActualsValueTypeDefault(isRetailer, props.isDistributor)

                if (isPbmAck && actualsValueType !== actualsValueTypeDefault) {
                  setActualsValueTypeForConfirm(actualsValueType)
                } else {
                  props.showSignOffDialog(true, ackItem)
                }
              }}
            >
              <Translation
                text={getSignOffButtonCaption(ackItem, isPartnerUser)}
                span
                actionsInside
              />
            </Button>
          )}

          {/* ------------------------------ Resend ------------------------------ */}
          {isPartnerAck && !!ackItem.CanResend && (
            <Button
              disabled={ackItem.CanResend === -1}
              className={styles.dropButton}
              color="primary"
              onClick={() => {
                props.showResendDialog(true, ackItem)
              }}
            >
              <Translation text="partnercommitment-resend" span actionsInside />
            </Button>
          )}

          {/* ------------------------------ Refuse ------------------------------ */}
          {(isManagerAck || isPartnerAck) && !!ackItem.CanRefuse && (
            <Button
              className={styles.dropButton}
              color="danger"
              disabled={ackItem.CanRefuse === -1}
              onClick={async () => {
                props.showRefuseDialog(true, ackItem)
              }}
            >
              <Translation text="partnercommitment-refuse" span actionsInside />
            </Button>
          )}

          {/* ------------------------------ Drop (PBM, Manager, Partner) ------------------------------ */}
          {!!ackItem.CanDrop && (
            <Button
              className={styles.dropButton}
              color="danger"
              disabled={ackItem.CanDrop === -1}
              onClick={() => {
                props.showDropDialog(true, { ...ackItem })
              }}
            >
              <Translation text="partnercommitment-drop-sign" span actionsInside />
            </Button>
          )}

          {/* ------------------------------ Manager only: [Manager is not identified], [Select] ------------------------------ */}
          {isManagerAck && !!(ackItem.CanChange || ackItem.CanSelect) && (
            <Button
              className={styles.dropButton}
              color="primary"
              disabled={ackItem.CanChange === -1 && ackItem.CanSelect === -1}
              onClick={() => {
                props.showManagerDialog(true, ackItem)
              }}
            >
              <Translation
                text={
                  !!ackItem.CanChange && ackItem.CommitmentType === 'manager'
                    ? 'jbp-commitment-manager-change'
                    : 'jbp-commitment-manager-select'
                }
                span
                actionsInside
              />
            </Button>
          )}

          {/* ------------------------------ Partner only ------------------------------ */}
          {isPartnerAck && !!(ackItem.CanChange || ackItem.CanSelect) && (
            <Button
              className={styles.dropButton}
              color="primary"
              disabled={ackItem.CanChange === -1 && ackItem.CanSelect === -1}
              onClick={() => {
                props.showSelectPartnerContactDialog(true, ackItem)
              }}
            >
              <Translation
                text={
                  !!ackItem.CanChange && ackItem.CommitmentType === 'partner'
                    ? 'partnercommitment-change-contact'
                    : 'partnercommitment-select-contact'
                }
                span
                actionsInside
              />
            </Button>
          )}

          {/* ------------------------------ Change PBM ------------------------------ */}
          <RenderWhen condition={isPbmAck && !!(ackItem.CanChange || ackItem.CanSelect)}>
            <Button
              className={styles.dropButton}
              color="primary"
              disabled={ackItem.CanChange === -1 && ackItem.CanSelect === -1}
              onClick={() => {
                props.showPbmDialog(true, ackItem)
              }}
            >
              <Translation text={'jbp-commitment-pbm-change'} span actionsInside />
            </Button>
          </RenderWhen>

          {/* ------------------------------ Manager only: [Confirm] and [change Manager] ------------------------------ */}
          {isManagerAck && !!ackItem.CanConfirmManager && (
            <>
              <div>
                <Button
                  className={styles.dropButton}
                  disabled={ackItem.CanConfirmManager === -1}
                  color="primary"
                  onClick={async () => {
                    props.confirmManager(ackItem)
                  }}
                >
                  <Translation text="jbp-commitment-confirm-manager" span actionsInside />
                </Button>
              </div>
            </>
          )}

          {!!ackItem.CanRemove && (
            <DeleteApprover
              disabled={ackItem.CanRemove === -1}
              onDeleteApprover={() => props.showDeleteDialog(ackItem)}
            />
          )}
        </div>
      </div>
      <PlanChangesLogDialog
        isOpen={isShowChangesDialog}
        onClose={() => setIsShowChangesDialog(false)}
        partnerId={props.pid}
        fYear={props.fyear}
        programType={props.programType}
        changeDate={ackItem.ActionDate}
        isDistributor={props.isDistributor}
      />

      {/** -------------------- Dialogs ----------------------- */}

      <ValueTypeConfirmDialog
        translationKey="jbp-exportPlan-valueType-dialog-ack"
        actualsValueType={actualsValueTypeForConfirm}
        show={actualsValueTypeForConfirm !== ActualsValueTypes.None}
        onConfirm={confirmActualsValueTypeHandler}
        onClose={closeConfirmActualsValueTypeDialogHandler}
      />
    </div>
  )
}
